import React from "react";
import Radio from "components/DesignSystem/RadioGroup/RadioGroup";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import Modal from "components/DesignSystem/Modal/Modal";
import { useFormikContext } from "formik";
import {
  PhysicalNexusTrackerFormValues,
  YES,
} from "./PhysicalNexusTrackerModal";
import { MultiValue, SingleValue } from "react-select";

const OfficeStepContent = ({
  stateOptions,
}: {
  stateOptions?: Array<{ value: string; label: string }>;
}) => {
  const { setFieldValue, values } =
    useFormikContext<PhysicalNexusTrackerFormValues>();

  const handleStateChange = (
    selected?: MultiValue<OptionData> | SingleValue<OptionData>
  ) => {
    if (Array.isArray(selected)) {
      setFieldValue(
        "office_in_us",
        selected.map((option) => option.value)
      );
    }
  };
  const stateMap = Object.fromEntries(
    stateOptions?.map((option) => [option.value, option.label]) || []
  );
  return (
    <div className="t-flex t-flex-col t-gap-5">
      <div className="t-flex t-flex-col t-gap-2">
        <p className="t-text-body t-text-text-100 t-w-full t-m-0">
          Do you have any offices in US?{" "}
          <span className="t-text-red t-font-bold t-text-body-sm">*</span>
        </p>

        <Radio.Root
          defaultValue={values.office_present}
          onValueChange={(value) => setFieldValue("office_present", value)}
        >
          <Radio.Content>
            <div className="t-flex t-flex-row t-gap-x-4 t-max-w-fit">
              <Radio.Item asChild value="yes">
                <span className="t-text-subtext t-text-text-100">Yes</span>
              </Radio.Item>
              <Radio.Item asChild value="no">
                <span className="t-text-subtext t-text-text-100">No</span>
              </Radio.Item>
            </div>
          </Radio.Content>
        </Radio.Root>
      </div>

      {values.office_present === YES && (
        <Combobox
          label="Add states where you have offices"
          placeholder="Add states"
          value={values.office_in_us?.map((i) => ({
            label: stateMap[i],
            value: i,
          }))}
          options={stateOptions}
          onChange={handleStateChange}
          isMulti
          menuPortalTarget={document.body}
          className="t-w-full"
          required
        />
      )}
    </div>
  );
};

const WarehouseStepContent = ({
  stateOptions,
}: {
  stateOptions?: Array<{ value: string; label: string }>;
}) => {
  const { setFieldValue, values } =
    useFormikContext<PhysicalNexusTrackerFormValues>();

  const handleStateChange = (
    selected?: MultiValue<OptionData> | SingleValue<OptionData>
  ) => {
    if (Array.isArray(selected)) {
      setFieldValue(
        "warehouse_in_us",
        selected.map((option) => option.value)
      );
    }
  };

  const stateMap = Object.fromEntries(
    stateOptions?.map((option) => [option.value, option.label]) || []
  );
  return (
    <div className="t-flex t-flex-col t-gap-5">
      <div className="t-flex t-flex-col t-gap-2">
        <p className="t-text-body t-text-text-100 t-w-full t-m-0">
          Do you have any warehouses in US?{" "}
          <span className="t-text-red t-font-bold t-text-body-sm">*</span>
        </p>
        <Radio.Root
          value={values.warehouse_present}
          onValueChange={(value) => {
            setFieldValue("warehouse_present", value);
          }}
        >
          <Radio.Content>
            <div className="t-flex t-flex-row t-gap-x-4 t-max-w-fit">
              <Radio.Item asChild value="yes">
                <span className="t-text-subtext t-text-text-100">Yes</span>
              </Radio.Item>
              <Radio.Item asChild value="no">
                <span className="t-text-subtext t-text-text-100">No</span>
              </Radio.Item>
            </div>
          </Radio.Content>
        </Radio.Root>
      </div>

      {values.warehouse_present === YES && (
        <Combobox
          label="Add states where you have warehouses"
          placeholder="Add states"
          options={stateOptions}
          value={values.warehouse_in_us?.map((i) => ({
            label: stateMap[i],
            value: i,
          }))}
          onChange={handleStateChange}
          isMulti={true}
          menuPortalTarget={document.body}
          className="t-w-full"
          required
        />
      )}
    </div>
  );
};

const EmployeeStepContent = ({
  stateOptions,
}: {
  stateOptions?: Array<{ value: string; label: string }>;
}) => {
  const { setFieldValue, values } =
    useFormikContext<PhysicalNexusTrackerFormValues>();

  const handleStateChange = (
    selected?: MultiValue<OptionData> | SingleValue<OptionData>
  ) => {
    if (Array.isArray(selected)) {
      setFieldValue(
        "employees_in_us",
        selected.map((option) => option.value)
      );
    }
  };
  const stateMap = Object.fromEntries(
    stateOptions?.map((option) => [option.value, option.label]) || []
  );
  return (
    <div className="t-flex t-flex-col t-gap-5">
      <div className="t-flex t-flex-col t-gap-2">
        <p className="t-text-body t-text-text-100 t-w-full t-m-0">
          Do you have any employee working in US?{" "}
          <span className="t-text-red t-font-bold t-text-body-sm">*</span>
        </p>
        <Radio.Root
          value={values.employees_present}
          onValueChange={(value) => setFieldValue("employees_present", value)}
        >
          <Radio.Content>
            <div className="t-flex t-flex-row t-gap-x-4 t-max-w-fit">
              <Radio.Item asChild value="yes">
                <span className="t-text-subtext t-text-text-100">Yes</span>
              </Radio.Item>
              <Radio.Item asChild value="no">
                <span className="t-text-subtext t-text-text-100">No</span>
              </Radio.Item>
            </div>
          </Radio.Content>
        </Radio.Root>
      </div>

      {values.employees_present === YES && (
        <Combobox
          label="Add states where you have employees"
          placeholder="Add states"
          options={stateOptions}
          value={values.employees_in_us?.map((i) => ({
            label: stateMap[i],
            value: i,
          }))}
          onChange={handleStateChange}
          isMulti
          menuPortalTarget={document.body}
          className="t-w-full"
          required
        />
      )}
    </div>
  );
};

export const PhysicalNexusTrackerModalBody = ({
  currentStep,
  stateOptions,
}: {
  currentStep: number;
  stateOptions?: Array<{ value: string; label: string }>;
}) => {
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <OfficeStepContent stateOptions={stateOptions} />;
      case 2:
        return <WarehouseStepContent stateOptions={stateOptions} />;

      default:
        return <EmployeeStepContent stateOptions={stateOptions} />;
    }
  };

  return (
    <>
      <Modal.Body>
        <div className="t-w-full t-flex t-flex-col t-gap-4">
          {renderStepContent()}
        </div>
      </Modal.Body>
    </>
  );
};
